import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

/**
 * OnboardingChecklist
 * ---------------------------------------------------------------------------
 * Friendly self-serve setup card shown at the top of the Staff Dashboard.
 * Auto-fetches /api/staff/onboarding-checklist and renders per-item status
 * with a progress bar. Auto-hides when every item is complete. Each item
 * has a "Do this" button that jumps to the relevant Staff Dashboard tab
 * (currently every item lives in the Profile tab).
 *
 * Props:
 *   userName         optional string — friendly greeting
 *   onNavigateTab    fn(tabKey)      — called to switch tabs (e.g. "profile")
 *   refreshTrigger   any             — bump to force a refetch after saves
 */
export function OnboardingChecklist({ userName, onNavigateTab, refreshTrigger }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getMyOnboardingChecklist();
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to load your setup checklist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshTrigger]);

  if (loading || error) {
    // Fail-quiet: if the endpoint hiccups we simply don't render the
    // card. It'll re-appear on next dashboard load. No noisy toast.
    return null;
  }
  if (!data || data.all_done) return null;
  // If nothing has a required flag AND completed is 0, it's probably a
  // non-staff user (parent hitting the endpoint) — hide the card.
  if (!Array.isArray(data.items) || data.items.length === 0) return null;

  const { items, completed, total, percent } = data;
  const firstName = (userName || '').split(' ')[0] || 'there';

  return (
    <div
      data-testid="onboarding-checklist-card"
      style={{
        background: 'linear-gradient(135deg, hsl(190 65% 97%) 0%, hsl(210 70% 98%) 100%)',
        border: '1px solid hsl(190 60% 80%)',
        borderRadius: 12,
        padding: '1.1rem 1.25rem',
        marginBottom: '1.25rem',
        boxShadow: '0 2px 8px hsl(190 40% 90% / 0.5)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 0.2rem 0', fontSize: '1.1rem', color: 'hsl(200 60% 22%)' }}>
            👋 Welcome, {firstName}! Let's finish your setup.
          </h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'hsl(200 30% 35%)' }}>
            {completed === 0
              ? `Complete these ${total} items so you can be scheduled and paid.`
              : completed >= Math.ceil(total * 0.75)
                ? `Almost there — just ${total - completed} to go!`
                : `${completed} of ${total} done — nice progress.`}
          </p>
        </div>
        <div
          data-testid="onboarding-progress-badge"
          style={{
            background: 'white',
            border: '1px solid hsl(190 40% 80%)',
            borderRadius: 999,
            padding: '0.35rem 0.75rem',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: 'hsl(200 60% 25%)',
            whiteSpace: 'nowrap',
          }}
        >
          {completed}/{total} · {percent}%
        </div>
      </div>

      {/* Progress bar */}
      <div
        aria-label={`Onboarding progress: ${percent}%`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        style={{
          height: 8,
          background: 'hsl(190 40% 92%)',
          borderRadius: 999,
          overflow: 'hidden',
          margin: '0.9rem 0 1rem 0',
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: 'linear-gradient(90deg, hsl(190 70% 55%) 0%, hsl(150 65% 50%) 100%)',
            transition: 'width 250ms ease',
          }}
        />
      </div>

      {/* Items */}
      <div style={{ display: 'grid', gap: '0.55rem' }}>
        {items.map((item) => (
          <OnboardingRow
            key={item.key}
            item={item}
            onDoThis={() => {
              if (onNavigateTab && item.tab) onNavigateTab(item.tab);
            }}
          />
        ))}
      </div>

      <div style={{ marginTop: '0.9rem', fontSize: '0.75rem', color: 'hsl(200 25% 45%)' }}>
        This card hides itself once every item is complete. Come back after saving your profile to see progress.
      </div>
    </div>
  );
}

function OnboardingRow({ item, onDoThis }) {
  const { key, label, icon, done, required, hint } = item;
  return (
    <div
      data-testid={`onboarding-item-${key}`}
      data-done={done ? 'true' : 'false'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.65rem',
        padding: '0.55rem 0.75rem',
        background: done ? 'hsl(140 55% 96%)' : 'white',
        border: `1px solid ${done ? 'hsl(140 50% 80%)' : (required ? 'hsl(38 90% 82%)' : 'hsl(210 20% 88%)')}`,
        borderRadius: 8,
        transition: 'background 200ms ease',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: done ? 'hsl(140 55% 45%)' : (required ? 'hsl(38 90% 55%)' : 'hsl(210 20% 82%)'),
          color: 'white',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.85rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {done ? '✓' : icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: done ? 'hsl(140 40% 25%)' : 'hsl(210 25% 20%)', textDecoration: done ? 'line-through' : 'none' }}>
          {label}
          {required && !done && (
            <span
              style={{
                marginLeft: 6,
                fontSize: '0.65rem',
                background: 'hsl(38 90% 92%)',
                color: 'hsl(30 80% 30%)',
                padding: '0.05rem 0.4rem',
                borderRadius: 4,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
              }}
              data-testid={`onboarding-required-badge-${key}`}
            >
              Required
            </span>
          )}
        </div>
        {!done && (
          <div style={{ fontSize: '0.72rem', color: 'hsl(210 15% 45%)', marginTop: 2 }}>
            {hint}
          </div>
        )}
      </div>
      {!done && (
        <button
          type="button"
          onClick={onDoThis}
          className="btn btn-primary"
          style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap' }}
          data-testid={`onboarding-doit-${key}`}
        >
          Do this →
        </button>
      )}
    </div>
  );
}

export default OnboardingChecklist;
